import { useEffect, useMemo, useRef, useState } from 'react'
import './style.scss'

interface SubFilterSectionProps {
	valueLabels: string[]
	value: number[] | undefined
	onValueUpdate: (newValue: number[] | undefined) => void
}

class SliderData {
	slidingMin: boolean = true
	parentSliderLeft: number = 0
	parentSliderWidth: number = 0
	eventX: number = 0
	minIndex: number = 0
	maxIndex: number = 0
}

const sliderData: SliderData = new SliderData()

function AppRangeSliderBasic({valueLabels, value, onValueUpdate}: SubFilterSectionProps) {
	const VALUE_INDICATOR_RADIUS: number = 10

	const rangeSliderRef = useRef<HTMLDivElement>(null)
	const [isSliding, setIsSliding] = useState<boolean>(false)
	const [mouseX, setMouseX] = useState<number>(0)

	const [rangeMinIndex, setRangeMinIndex] = useState<number>(0)
	const [rangeMaxIndex, setRangeMaxIndex] = useState<number>(valueLabels.length - 1)

	useEffect(() => {
		// make sure value indexes is an array at the correct length
		if (value !== undefined && value.length === 2) {
			setRangeMinIndex(value[0])
			setRangeMaxIndex(value[1])
			sliderData.minIndex = value[0]
			sliderData.maxIndex = value[1]
		} else {
			setRangeMinIndex(0)
			setRangeMaxIndex(valueLabels.length - 1)
			sliderData.minIndex = 0
			sliderData.maxIndex = valueLabels.length - 1
		}
	}, [value])

	// ================================== Range Slider ===================================

	const onSliderTouchDownMin = (event: React.MouseEvent | React.TouchEvent) => {
		sliderData.slidingMin = true
		onSliderTouchDown(event)
	}
	const onSliderTouchDownMax = (event: React.MouseEvent | React.TouchEvent) => {
		sliderData.slidingMin = false
		onSliderTouchDown(event)
	}

	const onSliderTouchDown = (event: React.MouseEvent | React.TouchEvent) => {
		const isTouchEvent = 'touches' in event
		const boundingRect: DOMRect | undefined = event.currentTarget.parentElement?.getBoundingClientRect()
		if (boundingRect) {
			sliderData.parentSliderLeft = boundingRect.left
		}
		sliderData.parentSliderWidth = event.currentTarget.parentElement?.clientWidth ? event.currentTarget.parentElement?.clientWidth : 0

		if (isTouchEvent) {
			setMouseX(event.touches[0].clientX)
			window.addEventListener('touchend', onSliderTouchUp)
			window.addEventListener('touchmove', onSliderTouchMove)
		} else {
			setMouseX(event.clientX)
			window.addEventListener('mouseup', onSliderMouseUp)
			window.addEventListener('mousemove', onSliderMouseMove)
		}
		setIsSliding(true)
	}

	const onSliderMouseMove = (event: MouseEvent) => {
		updateSliderValue(event.clientX)
	}
	const onSliderTouchMove = (event: TouchEvent) => {
		updateSliderValue(event.touches[0].clientX)
	}

	const onSliderMouseUp = (event: MouseEvent) => {
		updateSliderValue(event.clientX)
		finishSliderValue()
	}
	const onSliderTouchUp = (event: TouchEvent) => {
		if (event.changedTouches.length > 0) updateSliderValue(event.changedTouches[0].clientX)
		finishSliderValue()
	}

	const updateSliderValue = (x: number) => {
		setMouseX(x)
		const { parentSliderLeft, parentSliderWidth } = sliderData
		if (!parentSliderLeft || !parentSliderWidth) return

		const segmentWidth: number = (parentSliderWidth - (VALUE_INDICATOR_RADIUS * 2)) / (valueLabels.length - 1)
		let closestIndex: number = 0
		
		if (x < parentSliderLeft + VALUE_INDICATOR_RADIUS) {
			closestIndex = 0
		} else if (x > parentSliderLeft + parentSliderWidth - VALUE_INDICATOR_RADIUS) {
			closestIndex = valueLabels.length - 1
		} else {
			closestIndex = Math.floor(((x + (segmentWidth / 2)) - (parentSliderLeft + VALUE_INDICATOR_RADIUS)) / segmentWidth)
		}

		if (sliderData.slidingMin) {
			let minIndex: number = 0
			if (closestIndex > rangeMaxIndex - 1) {
				minIndex = rangeMaxIndex - 1
			} else if (closestIndex < 1) {
				minIndex = 0
			} else {
				minIndex = closestIndex
			}
			setRangeMinIndex(minIndex)
			sliderData.minIndex = minIndex
		} else {
			let maxIndex: number = 0
			if (closestIndex < rangeMinIndex + 1) {
				maxIndex = rangeMinIndex + 1
			} else if (closestIndex > valueLabels.length - 1) {
				maxIndex = valueLabels.length - 1
			} else {
				maxIndex = closestIndex
			}
			setRangeMaxIndex(maxIndex)
			sliderData.maxIndex = maxIndex
		}
	}

	const finishSliderValue = () => {
		setIsSliding(false)
		window.removeEventListener('mouseup', onSliderMouseUp)
		window.removeEventListener('mousemove', onSliderMouseMove)
		window.removeEventListener('touchend', onSliderTouchUp)
		window.removeEventListener('touchmove', onSliderTouchMove)
		if (sliderData.minIndex === 0 && sliderData.maxIndex === valueLabels.length - 1) {
			onValueUpdate(undefined)
		} else {
			onValueUpdate([sliderData.minIndex, sliderData.maxIndex])
		}
	}

	const sliderMinStyle = useMemo(
		() => ({
			marginLeft: isSliding && sliderData.slidingMin ? mouseX - sliderData.parentSliderLeft - VALUE_INDICATOR_RADIUS : `calc(((100% - ${VALUE_INDICATOR_RADIUS * 2}px)/${valueLabels.length-1})*${rangeMinIndex})`,
			background: isSliding && sliderData.slidingMin ? '#002da6' : '#2A50BB'
		}),
		[rangeMinIndex, isSliding, mouseX]
	)

	const sliderMaxStyle = useMemo(
		() => ({
			marginRight: isSliding && !sliderData.slidingMin ? sliderData.parentSliderLeft + sliderData.parentSliderWidth - mouseX - VALUE_INDICATOR_RADIUS : `calc(((100% - ${VALUE_INDICATOR_RADIUS * 2}px)/${valueLabels.length-1})*${(valueLabels.length - 1) - (rangeMaxIndex ? rangeMaxIndex : valueLabels.length-1)})`,
			background: isSliding && !sliderData.slidingMin ? '#002da6' : '#2A50BB'
		}),
		[rangeMaxIndex, isSliding, mouseX]
	)

	return (
		<div className='app-range-slider-basic-wrap'>
			<div 
				className='app-range-slider-basic-range-wrap'
				ref={rangeSliderRef}
			>
				<div 
					className={`app-range-slider-basic-range-indicator`}
					style={sliderMinStyle}
					onMouseDown={onSliderTouchDownMin}
					onTouchStart={onSliderTouchDownMin}
				/>
				<div 
					className={`app-range-slider-basic-range-indicator-seperator`}
				/>
				<div 
					className={`app-range-slider-basic-range-indicator`}
					style={sliderMaxStyle}
					onMouseDown={onSliderTouchDownMax}
					onTouchStart={onSliderTouchDownMax}
				/>
			</div>
			<div className='app-range-slider-basic-value-label-wrap'>
				{valueLabels.map((value: string, index) => 
					<div className='app-range-slider-basic-value-label' key={`selectValueLabel${index}`}>{value}</div>
				)}
			</div>
		</div>
	)
}

export default AppRangeSliderBasic
